import React, { useState, useEffect } from 'react';
import { collection, doc, setDoc, getDocs, updateDoc, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import * as XLSX from 'xlsx';

interface PublishResultsProps {
  domain: string;
}

interface ExamConfig {
  examType: string;
  academicYear: string;
  className: string;
  section: string;
}

interface StudentResult {
  studentId: string;
  studentName: string;
  rollNo: string;
  subjects: { [key: string]: SubjectMarks };
  totalScored: number;
  totalMarks: number;
  percentage: number;
  rank: number;
  overallGrade: string;
  remarks: string;
  attendance: string;
}

interface SubjectMarks {
  subjectName: string;
  scored: number;
  total: number;
  grade: string;
  remarks: string;
  teacherName: string;
}

interface ExamResult {
  examId: string;
  examName: string;
  examType: string;
  academicYear: string;
  className: string;
  section: string;
  term: string;
  subjects: string[];
  totalMarks: number;
  published: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  startDate: Timestamp;
  endDate: Timestamp;
}

const PublishResults: React.FC<PublishResultsProps> = ({ domain }) => {
  const [examConfig, setExamConfig] = useState<ExamConfig>({
    examType: 'Unit Test 1',
    academicYear: '2025-2026',
    className: '1',
    section: 'A',
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [existingExams, setExistingExams] = useState<ExamResult[]>([]);
  const [selectedExam, setSelectedExam] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [availableSubjects, setAvailableSubjects] = useState<string[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(false);

  const examTypes = [
    'Unit Test 1',
    'Unit Test 2',
    'Unit Test 3',
    'Mid Term',
    'Final Examination',
    'Quarterly',
    'Half Yearly',
    'Annual',
  ];

  const academicYears = ['2024-2025', '2025-2026', '2026-2027'];

  useEffect(() => {
    loadExistingExams();
    loadTermsAndSubjects();
  }, [domain, examConfig]);

  const loadExistingExams = async () => {
    setLoading(true);
    try {
      const examPath = `school-data/${domain}/exam-results`;
      const examsRef = collection(db, examPath);

      // Query exams matching current configuration
      const q = query(
        examsRef,
        where('className', '==', examConfig.className),
        where('section', '==', examConfig.section),
        where('academicYear', '==', examConfig.academicYear)
      );

      const snapshot = await getDocs(q);
      const exams: ExamResult[] = snapshot.docs.map(doc => ({
        ...doc.data(),
        examId: doc.id,
      } as ExamResult));

      setExistingExams(exams);

      // Auto-select if there's a matching exam
      const matchingExam = exams.find(e => e.examType === examConfig.examType);
      if (matchingExam) {
        setSelectedExam(matchingExam.examId);
      }
    } catch (error) {
      console.error('Error loading exams:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTermsAndSubjects = async () => {
    setLoadingConfig(true);
    try {
      // Load active subjects for the selected class
      const subjectsRef = collection(db, `school-data/${domain}/subjects`);
      const subjectsSnapshot = await getDocs(subjectsRef);

      const subjects: string[] = [];
      subjectsSnapshot.forEach((doc) => {
        const subjectData = doc.data();
        if (
          subjectData.isActive &&
          String(subjectData.className) === examConfig.className
        ) {
          subjects.push(subjectData.subjectName);
        }
      });

      setAvailableSubjects(subjects);
    } catch (error) {
      console.error('Error loading subjects:', error);
      // Fall back to default empty array if error
      setAvailableSubjects([]);
    } finally {
      setLoadingConfig(false);
    }
  };

  const generateExamId = (): string => {
    const { examType, className, section, academicYear } = examConfig;
    const typeSlug = examType.toLowerCase().replace(/\s+/g, '_');
    const yearSlug = academicYear.replace('-', '_');
    return `${typeSlug}_${className}_${section}_${yearSlug}`;
  };

  const calculateGrade = (percentage: number): string => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C+';
    if (percentage >= 40) return 'C';
    if (percentage >= 33) return 'D';
    return 'F';
  };

  const getRemarks = (grade: string): string => {
    const remarksMap: { [key: string]: string } = {
      'A+': 'Outstanding performance!',
      'A': 'Excellent',
      'B+': 'Very Good',
      'B': 'Good',
      'C+': 'Satisfactory performance. Focus on weak areas.',
      'C': 'Good',
      'D': 'Needs Improvement',
      'F': 'Needs Improvement',
    };
    return remarksMap[grade] || 'Keep working hard!';
  };

  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      // Check if subjects are configured
      if (availableSubjects.length === 0) {
        alert(
          `No subjects configured for Class ${examConfig.className}.\n\nPlease configure subjects first in the "Subjects" tab.`
        );
        setDownloadingTemplate(false);
        return;
      }

      // Fetch students from Firestore based on selected class and section
      const studentsRef = collection(db, `school-users/${domain}/students`);
      const snapshot = await getDocs(studentsRef);

      const templateData: any[] = [];

      snapshot.forEach((doc) => {
        const studentData = doc.data();
        // Filter by class and section
        if (
          String(studentData.class) === examConfig.className &&
          String(studentData.section) === examConfig.section
        ) {
          const row: any = {
            'Student ID': studentData.enrollmentId || doc.id,
            'Student Name': studentData.name || '',
            'Roll No': studentData.rollNumber || '',
          };

          // Add dynamic subject columns from Firestore
          availableSubjects.forEach((subject) => {
            row[subject] = '';
          });

          row['Attendance'] = '';

          templateData.push(row);
        }
      });

      // Sort by roll number
      templateData.sort((a, b) => {
        const rollA = parseInt(a['Roll No']) || 0;
        const rollB = parseInt(b['Roll No']) || 0;
        return rollA - rollB;
      });

      if (templateData.length === 0) {
        alert(
          `No students found for Class ${examConfig.className} Section ${examConfig.section}.\n\nPlease make sure students are enrolled in this class first.`
        );
        return;
      }

      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Results');

      const examId = generateExamId();
      XLSX.writeFile(wb, `${examId}_template.xlsx`);

      alert(
        `Template downloaded with ${templateData.length} students and ${availableSubjects.length} subjects from Class ${examConfig.className} Section ${examConfig.section}`
      );
    } catch (error: any) {
      console.error('Error downloading template:', error);
      alert(`Failed to download template: ${error.message}`);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadSuccess(false);
    }
  };

  const parseExcelFile = (file: File): Promise<StudentResult[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const results: StudentResult[] = [];

          // Get subject columns (all columns except Student ID, Student Name, Roll No, Attendance)
          const excludedColumns = ['Student ID', 'Student Name', 'Roll No', 'Attendance'];
          const sampleRow: any = jsonData[0];
          const subjectColumns = Object.keys(sampleRow).filter(
            col => !excludedColumns.includes(col)
          );

          jsonData.forEach((row: any) => {
            const subjects: { [key: string]: SubjectMarks } = {};
            let totalScored = 0;
            let totalMarks = 0;

            subjectColumns.forEach(subject => {
              const scored = Number(row[subject]) || 0;
              const total = 100; // Default total marks per subject
              const percentage = (scored / total) * 100;
              const grade = calculateGrade(percentage);

              subjects[subject] = {
                subjectName: subject,
                scored,
                total,
                grade,
                remarks: getRemarks(grade),
                teacherName: '',
              };

              totalScored += scored;
              totalMarks += total;
            });

            const percentage = (totalScored / totalMarks) * 100;
            const overallGrade = calculateGrade(percentage);

            results.push({
              studentId: String(row['Student ID']),
              studentName: String(row['Student Name']),
              rollNo: String(row['Roll No']),
              subjects,
              totalScored,
              totalMarks,
              percentage,
              rank: 0, // Will be calculated after sorting
              overallGrade,
              remarks: getRemarks(overallGrade),
              attendance: row['Attendance'] || '',
            });
          });

          // Calculate ranks
          results.sort((a, b) => b.totalScored - a.totalScored);
          results.forEach((result, index) => {
            result.rank = index + 1;
          });

          resolve(results);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = (error) => reject(error);
      reader.readAsBinaryString(file);
    });
  };

  const uploadResultsToFirestore = async () => {
    if (!selectedFile) {
      alert('Please select an Excel file first!');
      return;
    }

    setUploading(true);
    setUploadSuccess(false);

    try {
      // Parse Excel file
      const studentResults = await parseExcelFile(selectedFile);

      if (studentResults.length === 0) {
        alert('No data found in the Excel file!');
        return;
      }

      const examId = generateExamId();
      const examName = `${examConfig.examType} - Class ${examConfig.className}-${examConfig.section}`;

      // Get subject names from first student
      const subjects = Object.keys(studentResults[0].subjects);
      const totalMarks = studentResults[0].totalMarks;

      // Create exam document
      const examPath = `school-data/${domain}/exam-results`;
      const examDocRef = doc(db, examPath, examId);

      const now = Timestamp.now();

      const examData: ExamResult = {
        examId,
        examName,
        examType: examConfig.examType,
        academicYear: examConfig.academicYear,
        className: examConfig.className,
        section: examConfig.section,
        term: '1',
        subjects,
        totalMarks,
        published: false,
        createdAt: now,
        updatedAt: now,
        startDate: now,
        endDate: now,
      };

      await setDoc(examDocRef, examData);

      // Upload student results
      for (const result of studentResults) {
        const studentDocRef = doc(db, examPath, examId, 'student-results', result.studentId);

        const studentData = {
          studentId: result.studentId,
          studentName: result.studentName,
          rollNo: result.rollNo,
          className: examConfig.className,
          section: examConfig.section,
          academicYear: examConfig.academicYear,
          examId,
          examName,
          examType: examConfig.examType,
          examDate: now,
          subjects: result.subjects,
          totalScored: result.totalScored,
          totalMarks: result.totalMarks,
          percentage: result.percentage,
          rank: result.rank,
          overallGrade: result.overallGrade,
          remarks: result.remarks,
          attendance: result.attendance,
          classStrength: studentResults.length,
          term: '1',
          published: false,
          publishedDate: null,
        };

        await setDoc(studentDocRef, studentData);
      }

      setUploadSuccess(true);
      setSelectedExam(examId);
      alert(`Successfully uploaded results for ${studentResults.length} students!`);

      // Reload exams
      await loadExistingExams();
    } catch (error: any) {
      console.error('Error uploading results:', error);
      alert(`Failed to upload results: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handlePublishResults = async () => {
    if (!selectedExam) {
      alert('Please select an exam first!');
      return;
    }

    if (!confirm('Are you sure you want to make these results visible to students?')) {
      return;
    }

    setLoading(true);

    try {
      const examPath = `school-data/${domain}/exam-results`;
      const examDocRef = doc(db, examPath, selectedExam);

      // Update exam document
      await updateDoc(examDocRef, {
        published: true,
        updatedAt: Timestamp.now(),
      });

      // Update all student results
      const studentResultsRef = collection(db, examPath, selectedExam, 'student-results');
      const snapshot = await getDocs(studentResultsRef);

      const updatePromises = snapshot.docs.map(doc =>
        updateDoc(doc.ref, {
          published: true,
          publishedDate: Timestamp.now(),
        })
      );

      await Promise.all(updatePromises);

      alert('Results published successfully! Students can now view their results.');
      await loadExistingExams();
    } catch (error: any) {
      console.error('Error publishing results:', error);
      alert(`Failed to publish results: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUnpublishResults = async () => {
    if (!selectedExam) {
      alert('Please select an exam first!');
      return;
    }

    if (!confirm('Are you sure you want to hide these results from students?')) {
      return;
    }

    setLoading(true);

    try {
      const examPath = `school-data/${domain}/exam-results`;
      const examDocRef = doc(db, examPath, selectedExam);

      // Update exam document
      await updateDoc(examDocRef, {
        published: false,
        updatedAt: Timestamp.now(),
      });

      // Update all student results
      const studentResultsRef = collection(db, examPath, selectedExam, 'student-results');
      const snapshot = await getDocs(studentResultsRef);

      const updatePromises = snapshot.docs.map(doc =>
        updateDoc(doc.ref, {
          published: false,
          publishedDate: null,
        })
      );

      await Promise.all(updatePromises);

      alert('Results unpublished successfully! Students can no longer view their results.');
      await loadExistingExams();
    } catch (error: any) {
      console.error('Error unpublishing results:', error);
      alert(`Failed to unpublish results: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const selectedExamData = existingExams.find(e => e.examId === selectedExam);

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-graph-up me-2 text-primary-custom"></i>
          Exam Results Upload
        </h5>
        <small className="text-muted">Upload and publish exam results</small>
      </div>
      <div className="card-body">
        {/* Exam Configuration */}
        <div className="card mb-4" style={{ backgroundColor: '#f8f4ff' }}>
          <div className="card-body">
            <h6 className="mb-3">
              <i className="bi bi-gear me-2"></i>
              Exam Configuration
            </h6>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Exam Type</label>
                <select
                  className="form-select"
                  value={examConfig.examType}
                  onChange={(e) =>
                    setExamConfig({ ...examConfig, examType: e.target.value })
                  }
                >
                  {examTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">Academic Year</label>
                <select
                  className="form-select"
                  value={examConfig.academicYear}
                  onChange={(e) =>
                    setExamConfig({ ...examConfig, academicYear: e.target.value })
                  }
                >
                  {academicYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">Class</label>
                <select
                  className="form-select"
                  value={examConfig.className}
                  onChange={(e) =>
                    setExamConfig({ ...examConfig, className: e.target.value })
                  }
                >
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">Section</label>
                <select
                  className="form-select"
                  value={examConfig.section}
                  onChange={(e) =>
                    setExamConfig({ ...examConfig, section: e.target.value })
                  }
                >
                  {['A', 'B', 'C', 'D', 'E'].map((sec) => (
                    <option key={sec} value={sec}>
                      {sec}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Subjects Info */}
            {loadingConfig ? (
              <div className="alert alert-info mt-3">
                <span className="spinner-border spinner-border-sm me-2"></span>
                Loading subjects configuration...
              </div>
            ) : availableSubjects.length > 0 ? (
              <div className="alert alert-success mt-3">
                <i className="bi bi-check-circle me-2"></i>
                <strong>{availableSubjects.length} subjects</strong> configured for Class {examConfig.className}:{' '}
                {availableSubjects.join(', ')}
              </div>
            ) : (
              <div className="alert alert-warning mt-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                No subjects configured for Class {examConfig.className}. Please configure subjects in the "Subjects" tab first.
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="d-grid gap-3 mb-4">
          <button
            className="btn btn-outline-primary"
            onClick={downloadTemplate}
            disabled={downloadingTemplate}
          >
            {downloadingTemplate ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Loading Students...
              </>
            ) : (
              <>
                <i className="bi bi-download me-2"></i>
                Download Excel Template
              </>
            )}
          </button>

          <div>
            <label
              htmlFor="fileInput"
              className="btn btn-primary w-100"
              style={{ cursor: 'pointer' }}
            >
              <i className="bi bi-cloud-upload me-2"></i>
              Select Exam Results Excel File
            </label>
            <input
              id="fileInput"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {selectedFile && (
              <small className="text-muted d-block mt-2">
                <i className="bi bi-file-earmark-excel me-2"></i>
                {selectedFile.name}
              </small>
            )}
          </div>

          <button
            className="btn btn-primary"
            onClick={uploadResultsToFirestore}
            disabled={!selectedFile || uploading}
          >
            {uploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Uploading Results to Firestore...
              </>
            ) : (
              <>
                <i className="bi bi-cloud-upload me-2"></i>
                Upload Exam Results to Firestore
              </>
            )}
          </button>
        </div>

        {uploadSuccess && (
          <div className="alert alert-success">
            <i className="bi bi-check-circle me-2"></i>
            Results uploaded successfully!
          </div>
        )}

        {/* Publish Exam Results Section */}
        <div className="card">
          <div className="card-body">
            <h6 className="mb-3">
              <i className="bi bi-eye me-2"></i>
              Publish Exam Results
            </h6>
            <p className="text-muted small">
              Make results visible to students after uploading
            </p>

            {loading && (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {!loading && existingExams.length > 0 && (
              <>
                <div className="mb-3">
                  <label className="form-label">Selected Exam:</label>
                  <select
                    className="form-select"
                    value={selectedExam}
                    onChange={(e) => setSelectedExam(e.target.value)}
                  >
                    <option value="">Select an exam</option>
                    {existingExams.map((exam) => (
                      <option key={exam.examId} value={exam.examId}>
                        {exam.examName} - {exam.published ? '✓ Published' : '✗ Not Published'}
                      </option>
                    ))}
                  </select>
                  {selectedExamData && (
                    <small className="text-muted d-block mt-2">
                      Exam ID: {selectedExamData.examId}
                    </small>
                  )}
                </div>

                <div className="d-grid gap-2">
                  <button
                    className="btn btn-success"
                    onClick={handlePublishResults}
                    disabled={!selectedExam || loading || selectedExamData?.published}
                  >
                    <i className="bi bi-check-circle me-2"></i>
                    Publish Results (Make Visible to Students)
                  </button>

                  <button
                    className="btn btn-outline-danger"
                    onClick={handleUnpublishResults}
                    disabled={!selectedExam || loading || !selectedExamData?.published}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Unpublish Results (Hide from Students)
                  </button>
                </div>

                {selectedExamData && (
                  <div className={`alert mt-3 ${selectedExamData.published ? 'alert-success' : 'alert-warning'}`}>
                    <strong>Status:</strong>{' '}
                    {selectedExamData.published
                      ? 'Results are currently visible to students'
                      : 'Results are currently hidden from students'}
                  </div>
                )}
              </>
            )}

            {!loading && existingExams.length === 0 && (
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                No exams found for the selected configuration. Upload results first.
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default PublishResults;
